<h2>Privacy Policy</h2>
<h3>Account Registration Policies</h3>
<p>
    When you register an account, we collect identifiable information, such as
    your name, email address, and any other information explicitly provided by
    you. This data is stored in three databases: the application database, the
    Circle of Atonement's general contact list, and Sentry.io, a platform used
    solely for bug tracking and resolution in the app. We will not share or sell
    any of your information with third parties.
</p>
<p>
    We use <a
        href="https://sentry.io/security/"
        rel="noreferrer"
        target="_blank"
        class="link underline">Sentry.io</a
    > to identify and resolve bugs, ensuring the best possible user experience. When
    an error occurs in the app, relevant data, such as your user ID and the last
    few actions you performed, may be sent to Sentry.io.
</p>
<p>
    As you use the app, your tab locations, notes, and highlights are stored on
    the application database. This allows us to sync your settings across
    devices. This data is used exclusively to fulfill the functions of the
    application.
</p>
<h3>Email Communication Policy</h3>
<p>
    If you have not subscribed to our newsletter, we will only email you
    regarding crucial logistical information related to the app. For instance,
    we may notify you about new major releases or important steps to ensure your
    data is not lost due to technical issues. You can unsubscribe from this
    mailing list at any time through a link provided in the email. We will not
    send you marketing materials.
</p>
<h3>Newsletter</h3>
<p>
    By subscribing to our newsletter, you agree to receive emails about free and
    paid events, products, and workshops related to the Circle of Atonement. You
    can unsubscribe at any time through a link provided in the email.
</p>
<h3>Deleting Your Information</h3>
<p>
    You can delete your account from within the app. Doing so will permanently
    remove your information, including your email address, name, highlights, and
    tabs, from the application database. However, your contact information will
    remain in the Circle of Atonement's general contact list. While you will no
    longer receive logistical information about the app, you will continue to
    receive newsletter updates if you are subscribed.
</p>
<p>
    To completely remove your information from all our systems, please send a
    request to <a class="link underline" href="mailto:info@circleofa.org"
        >info@circleofa.org</a
    > from the associated email address.
</p>
<h3>Cookies</h3>
<p>Please refer to the Cookies section in the Terms of Use.</p>
<h2>Terms of Use</h2>
<p>
    Welcome to our application. By accessing and using this application, you
    agree to be bound by the following Terms of Use. Please read them carefully.
    If you do not agree to these terms, please do not use the application.
</p>
<h3>1. Account Registration</h3>
<p>
    When you register for an account, you agree to provide accurate and complete
    information about yourself. You are responsible for maintaining the
    confidentiality of your account information, including your password, and
    for all activities that occur under your account. You agree to notify us
    immediately of any unauthorized use of your account or any other breach of
    security.
</p>
<h3>2. Acceptable Use</h3>
<p>
    You agree to use the application only for lawful purposes and in a manner
    that does not infringe the rights of, restrict, or inhibit the use and
    enjoyment of the application by any third party. This includes, but is not
    limited to, conduct that is harassing, defamatory, or offensive or that
    disrupts the normal flow of dialogue within the application.
</p>
<p>
    You may create virtually unlimited highlights and notes within the
    application. However, we reserve the right to remove your account if we
    determine that you are abusing this feature or using it in a way that
    negatively impacts the performance of the application or the experience of
    other users.
</p>
<h3>3. Intellectual Property Rights</h3>
<p>
    All content and materials within the application, including but not limited
    to text, graphics, logos, and icons, are the property of the Circle of
    Atonement or its content suppliers and are protected by copyright and other
    intellectual property laws. You agree not to reproduce, duplicate, copy,
    sell, or exploit any portion of the application, its content, or any
    materials without express written permission from the Circle of Atonement.
</p>
<h3>4. Modifications to the Application and Terms of Use</h3>
<p>
    We reserve the right to modify or discontinue the application, or any part
    thereof, at any time, with or without notice. We also reserve the right to
    update these Terms of Use from time to time. Your continued use of the
    application following any such changes constitutes your acceptance of the
    revised Terms of Use.
</p>
<h3>5. Limitation of Liability</h3>
<p>
    The Circle of Atonement and its affiliates, licensors, and suppliers shall
    not be liable for any direct, indirect, incidental, special, consequential,
    or exemplary damages, including but not limited to damages for loss of
    profits, goodwill, use, data, or other intangible losses, resulting from the
    use or the inability to use the application, unauthorized access to or
    alteration of your data, or any other matter relating to the application.
</p>
<h3>6. Indemnification</h3>
<p>
    You agree to indemnify, defend, and hold harmless the Circle of Atonement
    and its affiliates, officers, directors, employees, agents, licensors, and
    suppliers from and against all claims, losses, liabilities, expenses,
    damages, and costs, including reasonable attorneys' fees, resulting from any
    violation of these Terms of Use or any activity related to your account by
    you or any other person accessing the application using your account.
</p>
<h3>7. Governing Law</h3>
<p>
    These Terms of Use shall be governed by and construed in accordance with the
    laws of the jurisdiction in which the Circle of Atonement operates. Any
    disputes arising from these Terms of Use shall be resolved in the courts of
    that jurisdiction.
</p>
<h3>8. Cookies</h3>
<p>
    We do not use cookies if you are not logged in. By registering and using the
    application, you consent to the use of cookies to improve your app
    experience and enable offline functionality.
</p>

<p>
    Cookies are used solely to support the app's functions. By registering for
    an account, you agree to allow us to store cookies on your device.
</p>
<h3>9. Termination</h3>
<p>
    We reserve the right, in our sole discretion, to terminate your access to
    the application and your account, with or without cause, and with or without
    prior notice. Upon termination, your account information, including
    highlights and notes, may be deleted or inaccessible. You agree that we
    shall not be liable to you or any third party for any termination of your
    access to the application or deletion of your data.
</p>
<h3>10. Privacy</h3>
<p>
    Please review our Privacy Policy, which is incorporated into these Terms of
    Use by reference, to understand our practices regarding the collection, use,
    and disclosure of your personal information.
</p>
<h3>11. Contact Information</h3>
<p>
    If you have any questions, concerns, or comments regarding these Terms of
    Use, please contact us at <a
        class="link underline"
        href="mailto:info@circleofa.org">info@circleofa.org</a
    >.
</p>
